<template>
  <v-app v-resize="onResize">
    <v-overlay v-if="isConnecting" color="#eeeeee" opacity="1">
      <v-progress-circular
        class="spinner"
        :size="140"
        :width="7"
        color="#3ABBDB"
        indeterminate
      />
    </v-overlay>
    <ChapiMenu v-model="drawer" v-if="showMenu" />
    <PreviewSidebar v-if="showMenu" />
    <v-main class="content-background">
      <div class="menu-button-wrapper" v-if="showMenu">
        <div class="menu-button" @click="setDrawer">
          <v-icon class="menu-button-icon" dark>{{
            drawer ? 'keyboard_arrow_left' : 'keyboard_arrow_right'
          }}</v-icon>
        </div>
      </div>
      <Snackbar />
      <LeaveConfirmModal />
      <WarningBanner v-if="showMenu" />
      <router-view />
      <transition name="preview-btn-appear">
        <v-btn
          v-if="showMenu"
          class="preview-toggle-btn"
          :class="{ 'preview-opened': isPreviewOpen }"
          @click="togglePreview"
        >
          <v-icon left>{{ isPreviewOpen ? 'close' : 'smartphone' }}</v-icon>
          {{ isPreviewOpen ? $t('PREVIEW.CLOSE') : $t('PREVIEW.OPEN') }}
        </v-btn>
      </transition>
    </v-main>
  </v-app>
</template>

<script lang="ts">
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration from 'dayjs/plugin/duration'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone' // dependent on utc plugin
import utc from 'dayjs/plugin/utc'
import Vue, { defineComponent } from 'vue'

import ChapiMenu from './components/Menu.vue'
import { publicRoutes } from './router'
import PreviewSidebar from './shared/components/preview/PreviewSidebar.vue'
import WarningBanner from './shared/components/WarningBanner.vue'
import LeaveConfirmModal from './shared/leaveConfirm/LeaveConfirmModal.vue'
import Snackbar from './shared/snackbar/SnackBar.vue'
import { mapGettersAppConfig } from './shared/store/app-config.store'
import { dispatchAppData } from './shared/store/app-data.store'
import { commitIsMobile } from './shared/store/isMobile.store'
import { dispatchPreview, mapStatePreview } from './shared/store/preview.store'
import { mapGettersSession } from './shared/store/session.store'
import store from './store'

dayjs.extend(utc)
dayjs.extend(customParseFormat)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(duration)
dayjs.extend(localizedFormat)
dayjs.locale('fr')
export default defineComponent({
  name: 'App',
  components: {
    ChapiMenu,
    WarningBanner,
    Snackbar,
    LeaveConfirmModal,
    PreviewSidebar,
  },
  data: () => ({
    drawer: true,
  }),

  async mounted() {
    dayjs.tz.setDefault(this.getTimezone)
    Vue.prototype.$dayjs = dayjs
    store.watch(
      (state) => state.appConfig.timezone,
      (newValue: string) => {
        dayjs.tz.setDefault(newValue)
        Vue.prototype.$dayjs = dayjs
      },
    )

    await dispatchAppData('loadCurrentData', { filterOnlyVisible: true })
  },

  computed: {
    ...mapGettersAppConfig(['getTimezone']),
    ...mapGettersSession(['isConnecting', 'isConnected']),
    ...mapStatePreview(['isOpen']),
    isPreviewOpen(): boolean {
      return this.isOpen
    },
    showMenu(): boolean {
      return (
        this.isConnected &&
        !this.isConnecting &&
        ![...publicRoutes, '/login'].includes(this.$route.path)
      )
    },
  },

  methods: {
    setDrawer() {
      this.drawer = !this.drawer
    },

    async togglePreview() {
      await dispatchPreview('toggleOpen')
    },

    onResize() {
      if (window.innerWidth < 769) {
        commitIsMobile('SET_IS_MOBILE')
      } else if (window.innerWidth < 1264) {
        commitIsMobile('SET_IS_SMALL_LAPTOP')
      } else {
        commitIsMobile('SET_IS_DEFAULT')
      }
    },
  },
})
</script>
<style lang="scss" scoped>
.v-application {
  font-family: 'Montserrat', 'Helvetica Neue', Arial, sans-serif;
}

.content-background {
  background-color: #eeeeee;
}

.menu-button {
  position: absolute;
  overflow: hidden;
  border-radius: 0 0 100% 0;
  height: 25px;
  background-color: #0f708a;
  width: 25px;
  cursor: pointer;
  top: 0;
}

.preview-toggle-btn {
  position: fixed;
  bottom: 0px;
  right: 0px;
  margin-bottom: 16px;
  margin-right: 16px;
  background-color: #4a5565 !important;
  color: white;
  border-radius: 28px;
  box-shadow:
    0 3px 5px -1px rgba(0, 0, 0, 0.2),
    0 6px 10px 0 rgba(0, 0, 0, 0.14),
    0 1px 18px 0 rgba(0, 0, 0, 0.12);
  z-index: 100;
  transition:
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.2s !important;

  &.preview-opened {
    transform: translateX(-450px);
  }
}

.menu-button-wrapper {
  position: sticky;
  height: 0px;
  width: 0px;
  left: 0;
  top: 0px;
  z-index: 6;
}

.menu-button-icon {
  margin: -6px 0 0 -3px;
  cursor: pointer;
}

.spinner {
  position: absolute;
  top: 50%;
  right: 50%;
  transform: translate(50%, -50%);
  text-align: center;
}

.preview-btn-appear-enter-active {
  transition:
    transform 0.3s ease-out,
    opacity 0.3s;
}

.preview-btn-appear-enter {
  transform: translateY(100px);
  opacity: 0;
}
</style>
